/**
 * @deprecated Import defaultDataForType from engine/nodes instead.
 * This file remains as a compatibility shim for M5 (entity-proxy) which has
 * not yet been migrated to a per-type folder. Once M5 ships, this file will
 * be deleted and createNode.ts will import directly from engine/nodes.
 */
import type { NodeType, EntityKind } from '../../utils/node-types';
import { defaultDataForType as registryDefault } from '../nodes';
import type { EntityProxyData, VisualEdgeData } from './types';
import type { ShapeData, StickyData, TextData, FreedrawData, FrameData, EmojiData, GifData } from '../nodes';

export type EntityProxyDisplayMode = 'card' | 'pill' | 'compact';

export function defaultDataForType(type: NodeType):
  ShapeData | StickyData | TextData | FreedrawData | FrameData | EntityProxyData | EmojiData | GifData {
  switch (type) {
    case 'shape':        return registryDefault('shape');
    case 'sticky':       return registryDefault('sticky');
    case 'text':         return registryDefault('text');
    case 'freedraw':     return registryDefault('freedraw');
    case 'frame':        return registryDefault('frame');
    case 'entity-proxy': return registryDefault('entity-proxy');
    case 'emoji':        return registryDefault('emoji');
    case 'gif':          return registryDefault('gif');
  }
}

// ── Text node helper ─────────────────────────────────────────────────────────

export interface MakeTextNodeDataOptions {
  text?: string;
}

/**
 * Creates a plain-object text node data payload with sensible defaults.
 * Mirrors the Text node's per-type defaults.ts makeDefault() so the
 * schema/defaults shim stays as the single source of truth for shell code.
 */
export function makeTextNodeData(opts: MakeTextNodeDataOptions = {}): TextData {
  return {
    text: opts.text ?? '',
    fontSize: 14,
    fontWeight: 400,
    color: 'var(--on-surface)',
    textAlign: 'left',
    verticalAlign: 'top',
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
  };
}

// ── EntityProxy node helper ───────────────────────────────────────────────────

export interface MakeEntityProxyDataOptions {
  entityId: string | null;
  entityKind: EntityKind;
  isVirtual?: boolean;
  virtualTitle?: string | null;
  tentative?: boolean;
  cachedTitle?: string;
  cachedSnippet?: string | null;
}

/**
 * Creates an EntityProxyData payload. Used by the resolver-paste handler
 * (shell/hooks/useResolverOnPaste) and any other shell code that needs to
 * create entity-proxy nodes without going through the engine node registry.
 *
 * Note: this lives in the schema/defaults shim (not engine/nodes/index.ts)
 * so shell code can import it without triggering the nodes barrel, which
 * pulls in ConnectedEntityProxyNode and therefore shell imports.
 */
// ── Edge helpers ──────────────────────────────────────────────────────────

/**
 * Default visual-edge data payload for newly-created connectors. Centralised
 * so the create-edge code (drag-from-dot, connector tool) and the floating
 * toolbar agree on the same starting state.
 */
export function makeVisualEdgeData(): VisualEdgeData {
  return {
    label: '',
    // Same neutral gray we use for shape outlines so connectors visually
    // pair with the node chrome.
    stroke: 'var(--outline-variant)',
    strokeWidth: 2,
    strokeStyle: 'solid',
    markerStart: 'none',
    markerEnd: 'arrow',
    curvature: 'smooth',
  };
}

export function makeEntityProxyData(opts: MakeEntityProxyDataOptions): EntityProxyData {
  return {
    entityKind: opts.entityKind,
    entityId: opts.entityId,
    isVirtual: opts.isVirtual ?? false,
    virtualTitle: opts.virtualTitle ?? null,
    cachedTitle: opts.cachedTitle ?? '',
    cachedSnippet: opts.cachedSnippet ?? null,
    cachedUpdatedAt: Date.now(),
    tentative: opts.tentative ?? false,
    displayMode: 'card',
    showPreview: true,
  };
}
